// Public entrypoint for the shift-plot module (#14): a baseline-versus-
// comparison scatter that reuses the lifecycle API, control shell, and linked
// listing proven by the histogram pilot. Behavior tracks the original
// RhoInc/safety-shift-plot renderer (measure + baseline/comparison visit
// controls, per-participant shift pairing, identity line, brush-to-list
// selection, participant counts, invalid-result note) without depending on its
// Webcharts/d3 internals. Internals follow the histogram module flow
// (checkInputs → configure → structureData → getScales/getPlugins → new Chart).

import {
  Chart,
  ScatterController,
  PointElement,
  LinearScale,
  LogarithmicScale,
  Tooltip,
  Legend
} from 'chart.js';

import { controlBuilders, createElement, option, renderShell } from './shell.js';
import { AXIS_TYPES, syncSettings } from './shift-plot/configure.js';
import { checkInputs } from './shift-plot/checkInputs.js';
import {
  applyFilters,
  cleanData,
  computeDomain,
  computeShiftPairs,
  listVisits,
  measureLabel,
  unique
} from './shift-plot/structureData.js';
import { buildScales } from './shift-plot/getScales.js';
import {
  brushBoxPlugin,
  COLORS,
  identityLinePlugin,
  pointColors,
  tooltipLines
} from './shift-plot/getPlugins.js';
import { renderListing } from './histogram/listing.js';
import {
  buildProfileRows,
  mountProfileRail,
  resetProfileRail,
  syncProfileRail,
  unmountProfileRail
} from './profile-host.js';
import { initFilterState, reconcileFilters, renderFilterControl } from './filters.js';
import { presentMeasures, resolveMeasureList } from './measure-list.js';

Chart.register(ScatterController, PointElement, LinearScale, LogarithmicScale, Tooltip, Legend);

const INITIAL_FOOTNOTE = 'Click and drag across the points to list the selected participants.';

/**
 * Interactive safety shift plot: a Chart.js scatter of each participant's
 * baseline-visit value (x) against their comparison-visit value (y) for a
 * chosen measure, with an identity reference line, measure and baseline/
 * comparison visit controls, configurable filters, participant counts, and a
 * brush selection that opens a linked participant listing. Construct via the
 * shiftPlot() factory rather than directly; the constructor renders the
 * control shell immediately and waits for data.
 */
class SafetyShiftPlot {
  constructor(element = 'body', settings = {}) {
    this.element = typeof element === 'string' ? document.querySelector(element) : element;
    if (!this.element) throw new Error(`Safety Shift Plot target not found: ${element}`);
    this.settings = syncSettings(settings);
    this.rawData = [];
    this.cleanData = [];
    this.availableMeasures = [];
    this.chartPairs = [];
    this.currentTableData = [];
    this.listingSearch = '';
    this.listingSort = null;
    this.page = 1;
    this.brushing = false;
    this.chart = null;
    // The railed participant-profile module (#99, PPRF-SSP): the shared
    // drill-down rendered into the shell's profile slot and fed by the brush
    // selection through dispatchSelected's participantsSelected event on the
    // shell root. A multi-point brush collapses the dock to the worst-first
    // cohort stepper (PPRF-SSP-001). profileRows is the ONE per-setData
    // profile ingest (hep-core cleaned rows); profileKey is the idempotency
    // guard.
    this.profile = null;
    this.profileFeed = null;
    this.profileKey = null;
    this.profileRows = [];
    this.state = this.seedState();
    this.renderShell();
    mountProfileRail(this, () => this.profileSettings());
  }

  /**
   * The opening control state: every settings-derived default, before the
   * data-driven normalisers (the measure fallback and resolveVisits) run.
   * Called from the constructor and from reseed(), so the Reset chart control
   * and the first render agree by construction (SSP-CTRL-004, #136).
   * @private
   */
  seedState() {
    return {
      measure: this.settings.start_value,
      baselineVisits: this.settings.baseline_visits,
      comparisonVisits: this.settings.comparison_visits,
      baselineStat: this.settings.baseline_stat,
      comparisonStat: this.settings.comparison_stat,
      filters: initFilterState(this.settings.filters),
      axisType: this.settings.axis_type,
      domain: null
    };
  }

  /**
   * Restore the opening state for the Reset chart control (SSP-CTRL-004,
   * #136): the settings-derived seed, then the two normalisers that run after
   * it on first load — the measure fallback and the baseline/comparison visit
   * resolution (SSP-CFG-004/005). All three of `start_value`,
   * `baseline_visits` and `comparison_visits` default to null, so skipping
   * them would hand back a blank chart with two empty visit controls.
   * `domain: null` needs no normalisation — render() recomputes it. Does NOT
   * re-clean the data: nothing about the data changed.
   * @private
   */
  reseed() {
    this.state = this.seedState();
    if (this.cleanData.length) {
      this.resolveMeasure();
      this.resolveVisits();
    }
  }

  /**
   * The settings handed to the railed participant-profile module (#99,
   * PPRF-SSP-002): the long-lab column mappings pass through — visitn_col maps
   * from the host's visit_order_col — with the profile fed from the retained
   * rawData (NOT the pair-per-participant chartPairs); `details` come from
   * profile_details (the host `details` configure the linked listing — pair
   * columns, not demographics); and the two outbound callbacks wire Clear to
   * the host's own clear path and stepper navigation to a transient border
   * emphasis (no dispatch).
   * @private
   */
  profileSettings() {
    const settings = this.settings;
    const profileSettings = {
      id_col: settings.id_col,
      measure_col: settings.measure_col,
      value_col: settings.value_col,
      unit_col: settings.unit_col,
      normal_col_high: settings.normal_col_high,
      normal_col_low: settings.normal_col_low,
      studyday_col: settings.studyday_col,
      visit_col: settings.visit_col,
      visitn_col: settings.visit_order_col,
      details:
        settings.profile_details && settings.profile_details.length ? settings.profile_details : [],
      participantProfileURL: settings.participantProfileURL ?? null,
      on_clear: () => this.clearSelection(),
      on_step: (id) => this.emphasizeParticipant(id)
    };
    // Only forward a caller-supplied key-measure map — null keeps the profile
    // module's own ALT/AST/TB/ALP defaults.
    if (settings.measure_values) profileSettings.measure_values = settings.measure_values;
    return profileSettings;
  }

  /**
   * Transient chart emphasis for the profile stepper (PPRF-SSP-001, PPRF-11):
   * border-highlight the stepped participant's point without touching the
   * brush selection ($sspSelected stays) and without dispatching — the host
   * selection still belongs to the brush gesture. Each step recomputes the
   * emphasis, and clearSelection restores the uniform border.
   * @private
   */
  emphasizeParticipant(id) {
    if (!this.chart) return;
    const index = this.chartPairs.findIndex(
      (pair) => String(pair[this.settings.id_col]) === String(id)
    );
    const dataset = this.chart.data.datasets[0];
    dataset.borderWidth =
      index < 0 ? 1 : this.chartPairs.map((pair, pairIndex) => (pairIndex === index ? 3 : 1));
    this.chart.update('none');
  }

  /**
   * Build the static DOM shell the scatter and listing render into.
   * @private
   */
  renderShell() {
    Object.assign(
      this,
      renderShell(this.element, {
        moduleClass: 'safety-shift-plot',
        onToggle: () => this.resize()
      })
    );
    this.footnote.textContent = INITIAL_FOOTNOTE;
  }

  /**
   * Load data and render: an alias for setData that keeps the pilot's
   * two-step create-then-init call shape working (SSP-DATA-003).
   * @param {Object[]} data Long-format result records matching the shift-plot data contract.
   * @returns {SafetyShiftPlot} The instance, for chaining.
   */
  init(data) {
    this.setData(data);
    return this;
  }

  /**
   * Replace the bound data and re-render. The data is validated against the
   * settings mapping (throwing, and rendering the message into the target
   * element, when required columns are missing), rows with missing or
   * non-numeric results are removed with a console warning, and the controls
   * are rebuilt from the new data's measures and visits.
   * @param {Object[]} data Long-format result records matching the shift-plot data contract.
   * @returns {SafetyShiftPlot} The instance, for chaining.
   */
  setData(data) {
    this.rawData = Array.isArray(data) ? data : [];
    this.validateAndCleanData();
    this.buildProfileRows();
    this.buildControls();
    this.render();
    return this;
  }

  /**
   * Derive the railed profile's pre-cleaned rows ONCE per data/settings change
   * (#99, PPRF-SSP-002) — never per gesture, and from the retained rawData
   * rather than the pair-per-participant chartPairs (the profile narrates the
   * full series, not the baseline/comparison collapse).
   * @private
   */
  buildProfileRows() {
    this.profileRows = this.settings.profile
      ? buildProfileRows(this.rawData, this.profileSettings())
      : [];
  }

  /**
   * Merge setting overrides onto the current settings, re-normalize them (same
   * rules as the factory), rebuild the controls, and re-render.
   * @param {ShiftPlotSettings} settings Setting overrides to merge.
   * @returns {SafetyShiftPlot} The instance, for chaining.
   */
  setSettings(settings) {
    this.settings = syncSettings({ ...this.settings, ...settings });
    // Re-clean so a changed measure whitelist re-resolves the control and
    // re-validates the selected measure; every sibling renderer already does
    // this from setSettings, and shift-plot alone did not (#136).
    if (this.rawData.length) this.validateAndCleanData();
    this.state.baselineStat = this.settings.baseline_stat;
    this.state.comparisonStat = this.settings.comparison_stat;
    this.state.axisType = this.settings.axis_type;
    if (settings.baseline_visits !== undefined)
      this.state.baselineVisits = this.settings.baseline_visits;
    if (settings.comparison_visits !== undefined)
      this.state.comparisonVisits = this.settings.comparison_visits;
    this.resolveVisits();
    this.buildProfileRows();
    syncProfileRail(this, () => this.profileSettings());
    this.buildControls();
    this.render();
    return this;
  }

  /**
   * Validate the raw data against the settings mapping and drop unusable rows,
   * then resolve the default measure and visit selections.
   * @private
   */
  validateAndCleanData() {
    try {
      checkInputs(this.rawData, this.settings);
    } catch (error) {
      // The message is inserted as text: it names columns from the settings,
      // which a host may fill from a file header, and must never become markup.
      this.element.replaceChildren(createElement('div', 'sv-warning', error.message));
      throw error;
    }
    const { rows, removed } = cleanData(this.rawData, this.settings);
    this.cleanData = rows;
    this.removedRecords = removed;
    if (removed) console.warn(`${removed} missing or non-numeric results have been removed.`);
    this.availableMeasures = resolveMeasureList(
      presentMeasures(this.cleanData, this.settings, measureLabel),
      this.settings.measures
    );
    this.resolveMeasure();
    this.resolveVisits();
  }

  /**
   * Pin the selected measure to one the data actually carries, warning when a
   * configured measure is absent (SSP-CTRL-001, SSP-MEAS-002). Runs after the
   * settings-derived seed on both paths that produce an opening state —
   * validateAndCleanData and reseed — because `start_value` defaults to null
   * and the opening measure is therefore data-derived, not settings-derived.
   * @private
   */
  resolveMeasure() {
    const measures = this.measures();
    if (this.state.measure && !measures.includes(this.state.measure)) {
      console.warn(
        `The initial measure [${this.state.measure}] does not exist. Defaulting to the first measure.`
      );
    }
    this.state.measure = measures.includes(this.state.measure) ? this.state.measure : measures[0];
  }

  /**
   * The measure labels the Measure control offers: the configured `measures`
   * whitelist in its own order, or every measure in the cleaned data
   * alphabetically when it is unset (#136).
   * @private
   */
  measures() {
    return this.availableMeasures;
  }

  /**
   * Ordered distinct visit labels present in the cleaned data.
   * @private
   */
  visits() {
    return listVisits(this.cleanData, this.settings);
  }

  /**
   * Resolve the baseline/comparison visit selections against the current data:
   * an unset baseline defaults to the first visit, an unset comparison to
   * every visit after the baseline, and selections naming absent visits are
   * dropped (SSP-CFG-004/005).
   * @private
   */
  resolveVisits() {
    const visits = this.visits();
    let baseline = (this.state.baselineVisits || []).filter((visit) => visits.includes(visit));
    if (!baseline.length) baseline = visits.length ? [visits[0]] : [];
    let comparison = (this.state.comparisonVisits || []).filter((visit) => visits.includes(visit));
    if (!comparison.length) comparison = visits.filter((visit) => !baseline.includes(visit));
    this.state.baselineVisits = baseline;
    this.state.comparisonVisits = comparison;
  }

  /**
   * Rebuild the measure, baseline/comparison visit, and filter controls from
   * data + state.
   * @private
   */
  buildControls() {
    this.controls.innerHTML = '';
    const { addSection, addControl, addReset } = controlBuilders(this.controls);
    const visits = this.visits();

    const measure = addControl('Measure', document.createElement('select'));
    this.measures().forEach((value) => option(measure, value, value, value === this.state.measure));
    measure.onchange = () => {
      this.state.measure = measure.value;
      this.render();
    };

    const axisSection = addSection('Axis');
    const axisType = addControl('Axis Type', document.createElement('select'), axisSection);
    AXIS_TYPES.forEach((value) => option(axisType, value, value, value === this.state.axisType));
    axisType.onchange = () => {
      this.state.axisType = axisType.value;
      this.render();
    };

    const visitSection = addSection('Visits');
    const baseline = addControl(
      'Baseline visit(s)',
      document.createElement('select'),
      visitSection
    );
    baseline.multiple = true;
    baseline.size = Math.min(Math.max(visits.length, 2), 6);
    visits.forEach((visit) =>
      option(baseline, visit, visit, this.state.baselineVisits.includes(visit))
    );
    baseline.onchange = () => {
      this.state.baselineVisits = Array.from(baseline.selectedOptions).map((opt) => opt.value);
      this.render();
    };

    const comparison = addControl(
      'Comparison visit(s)',
      document.createElement('select'),
      visitSection
    );
    comparison.multiple = true;
    comparison.size = Math.min(Math.max(visits.length, 2), 6);
    visits.forEach((visit) =>
      option(comparison, visit, visit, this.state.comparisonVisits.includes(visit))
    );
    comparison.onchange = () => {
      this.state.comparisonVisits = Array.from(comparison.selectedOptions).map((opt) => opt.value);
      this.render();
    };

    const filterSpecs = this.settings.filters.filter((filter) => {
      const exists = this.cleanData.some((row) => row[filter.value_col] !== undefined);
      if (!exists)
        console.warn(
          `The [ ${filter.label} ] filter has been removed because the variable does not exist.`
        );
      return exists;
    });
    const filterControls = reconcileFilters(this.state.filters, filterSpecs, (filter) =>
      unique(this.cleanData.map((row) => row[filter.value_col])).sort()
    );
    if (filterControls.length) {
      const filterParent = addSection('Filters');
      filterControls.forEach(({ spec: filter, values, selected }) => {
        addControl(
          filter.label,
          renderFilterControl({
            spec: filter,
            values,
            selected,
            onChange: (next) => {
              this.state.filters[filter.value_col] = next;
              this.render();
            }
          }),
          filterParent
        );
      });
    }

    // Reset chart (SSP-CTRL-004, #136): the whole-chart way back to the
    // opening view. Appended straight to the controls container as the LAST
    // statement of buildControls so it sits full-width below every section.
    addReset(() => {
      this.reseed();
      this.buildControls();
      this.render();
    });
  }

  /**
   * Cleaned rows for the selected measure.
   * @private
   */
  currentMeasureData() {
    return this.cleanData.filter((row) => measureLabel(row, this.settings) === this.state.measure);
  }

  /**
   * Cleaned rows for the selected measure after the active filters.
   * @private
   */
  currentFilteredData() {
    return applyFilters(this.currentMeasureData(), this.state.filters);
  }

  /**
   * The baseline/comparison pairs for the current measure, visits, and filters.
   * @private
   */
  computePairs() {
    return computeShiftPairs({
      rows: this.currentFilteredData(),
      measure: this.state.measure,
      baselineVisits: this.state.baselineVisits,
      comparisonVisits: this.state.comparisonVisits,
      baselineStat: this.state.baselineStat,
      comparisonStat: this.state.comparisonStat,
      settings: this.settings
    });
  }

  /**
   * Redraw everything from the current data, settings, and control state:
   * destroys the live chart, clears the listing and any brush selection, then
   * draws the scatter, the identity line, and the participant-count notes.
   * Called automatically by the controls and the data/settings setters; call
   * it directly only after mutating state by hand.
   * @returns {void}
   */
  render() {
    this.destroyChart();
    this.listingWrap.innerHTML = '';
    this.currentTableData = [];
    this.listingSearch = '';
    this.listingSort = null;
    this.page = 1;
    this.footnote.textContent = INITIAL_FOOTNOTE;
    // The brush selection resets silently on every render, so the rail must
    // empty in the same preamble (#99, PPRF-SSP-003).
    resetProfileRail(this);
    this.notes.innerHTML = '';
    // A logarithmic axis has no room for 0 or a negative value, so a pair
    // whose baseline OR comparison value is non-positive is REMOVED rather
    // than clamped — moving a participant's result would be a lie on a
    // clinical chart. The removal is counted and reported in the note
    // (SSP-SCALE-003), following results-over-time's precedent. The pair is
    // dropped when EITHER coordinate is non-positive, because one domain
    // governs both axes.
    let pairs = this.computePairs();
    let nonPositive = 0;
    if (this.state.axisType === 'log') {
      const positive = pairs.filter((pair) => pair.x > 0 && pair.y > 0);
      nonPositive = pairs.length - positive.length;
      pairs = positive;
    }
    this.chartPairs = pairs;
    this.state.domain = computeDomain(this.chartPairs, this.state.axisType);
    this.updateNotes(nonPositive);
    if (!this.chartPairs.length) {
      this.footnote.textContent =
        'No participant has both a baseline and a comparison value for the current selection.';
      return;
    }
    this.drawChart();
  }

  /**
   * Draw the Chart.js scatter with tooltips, the identity line, and the brush
   * selection.
   * @private
   */
  drawChart() {
    const chart = new Chart(this.canvas.getContext('2d'), {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: this.state.measure,
            data: this.chartPairs.map((pair) => ({ x: pair.x, y: pair.y })),
            backgroundColor: COLORS.point,
            borderColor: COLORS.border,
            borderWidth: 1,
            pointRadius: 4,
            pointHoverRadius: 6
          }
        ]
      },
      options: {
        maintainAspectRatio: false,
        responsive: true,
        animation: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => tooltipLines(this.chartPairs[ctx.dataIndex], this.settings.id_col)
            }
          }
        },
        scales: buildScales(this.state.domain, this.state.measure, this.state.axisType)
      },
      plugins: [identityLinePlugin(this), brushBoxPlugin()]
    });
    this.chart = chart;
    this.attachBrush(chart);
  }

  /**
   * Wire the click-drag brush on the chart canvas: dragging paints the gray
   * selection rectangle; releasing selects the enclosed points (or clears the
   * selection when the drag is an empty click) (SSP-REQ-003, SSP-REG-004/012).
   * @private
   */
  attachBrush(chart) {
    const canvas = chart.canvas;
    const clamp = (value, lo, hi) => Math.max(lo, Math.min(hi, value));
    const position = (event) => {
      const rect = canvas.getBoundingClientRect();
      const area = chart.chartArea;
      return {
        x: clamp(event.clientX - rect.left, area.left, area.right),
        y: clamp(event.clientY - rect.top, area.top, area.bottom)
      };
    };
    let start = null;
    const onDown = (event) => {
      start = position(event);
      this.brushing = true;
    };
    const onMove = (event) => {
      if (!this.brushing || !start) return;
      const point = position(event);
      chart.$sspBrush = {
        left: Math.min(start.x, point.x),
        right: Math.max(start.x, point.x),
        top: Math.min(start.y, point.y),
        bottom: Math.max(start.y, point.y)
      };
      chart.draw();
    };
    const onUp = (event) => {
      if (!this.brushing || !start) return;
      this.brushing = false;
      const point = position(event);
      const rect = {
        left: Math.min(start.x, point.x),
        right: Math.max(start.x, point.x),
        top: Math.min(start.y, point.y),
        bottom: Math.max(start.y, point.y)
      };
      start = null;
      if (rect.right - rect.left < 3 && rect.bottom - rect.top < 3) {
        this.clearSelection();
        return;
      }
      this.selectInPixelRect(rect);
    };
    canvas.addEventListener('mousedown', onDown);
    canvas.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    chart.$sspBrushCleanup = () => {
      canvas.removeEventListener('mousedown', onDown);
      canvas.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }

  /**
   * Select every point whose pixel position falls inside a rectangle, opening
   * the listing (or clearing when the rectangle catches nothing).
   * @param {{left:number,right:number,top:number,bottom:number}} rect Pixel rectangle.
   * @returns {void}
   */
  selectInPixelRect(rect) {
    if (!this.chart) return;
    const meta = this.chart.getDatasetMeta(0);
    const selected = new Set();
    meta.data.forEach((element, index) => {
      if (
        element.x >= rect.left &&
        element.x <= rect.right &&
        element.y >= rect.top &&
        element.y <= rect.bottom
      )
        selected.add(index);
    });
    if (!selected.size) {
      this.clearSelection();
      return;
    }
    this.showSelection(selected, rect);
  }

  /**
   * Select points inside a data-space rectangle. The programmatic entry point
   * the R widget bindings and tests use in place of a mouse drag.
   * @param {number} x0 One baseline-axis bound of the rectangle.
   * @param {number} x1 The other baseline-axis bound.
   * @param {number} y0 One comparison-axis bound of the rectangle.
   * @param {number} y1 The other comparison-axis bound.
   * @returns {void}
   */
  brushValues(x0, x1, y0, y1) {
    if (!this.chart) return;
    const { scales } = this.chart;
    this.selectInPixelRect({
      left: scales.x.getPixelForValue(Math.min(x0, x1)),
      right: scales.x.getPixelForValue(Math.max(x0, x1)),
      top: scales.y.getPixelForValue(Math.max(y0, y1)),
      bottom: scales.y.getPixelForValue(Math.min(y0, y1))
    });
  }

  /**
   * Record the selection, de-emphasize the unselected points, draw the gray
   * box, and open the listing (SSP-REQ-003/006/007, SSP-REG-004).
   * @private
   */
  showSelection(selected, rect) {
    const dataset = this.chart.data.datasets[0];
    dataset.backgroundColor = pointColors(this.chartPairs.length, selected, COLORS.point);
    dataset.borderColor = pointColors(this.chartPairs.length, selected, COLORS.border);
    // A fresh brush clears any transient stepper emphasis (PPRF-SSP-001).
    dataset.borderWidth = 1;
    this.chart.$sspBrush = rect;
    this.chart.$sspSelected = selected;
    this.chart.update('none');
    this.currentTableData = [...selected].map((index) => this.chartPairs[index]);
    this.listingSearch = '';
    this.listingSort = null;
    this.page = 1;
    renderListing(this);
    this.footnote.textContent = `Selected ${this.currentTableData.length} participant(s).`;
    this.dispatchSelected(this.currentTableData.map((pair) => pair[this.settings.id_col]));
  }

  /**
   * Clear the brush selection: restore uniform point colors, hide the gray box
   * and the listing, and dispatch an empty participantsSelected event
   * (SSP-REG-011).
   * @returns {void}
   */
  clearSelection() {
    if (this.chart) {
      const dataset = this.chart.data.datasets[0];
      dataset.backgroundColor = COLORS.point;
      dataset.borderColor = COLORS.border;
      dataset.borderWidth = 1;
      this.chart.$sspBrush = null;
      this.chart.$sspSelected = null;
      this.chart.update('none');
    }
    this.currentTableData = [];
    this.listingWrap.innerHTML = '';
    this.footnote.textContent = INITIAL_FOOTNOTE;
    this.dispatchSelected([]);
  }

  /**
   * Dispatch the participantsSelected event on the shell root with the
   * selected IDs (SSP-API-003, PPRF-SSP-004). The target moved from the host
   * element to the shell root in the rail adoption (#99) so root-level
   * listeners — the railed profile's feed — hear every dispatch; the event
   * still bubbles, so element-level listeners keep working.
   * @private
   */
  dispatchSelected(ids) {
    this.root.dispatchEvent(
      new CustomEvent('participantsSelected', { detail: { data: ids }, bubbles: true })
    );
  }

  /**
   * Refresh the shown/total participant counts and the removed-record notes
   * (SSP-COUNT-001, SSP-REG-005/020). The shown count is chartPairs.length, so
   * pairs dropped for the log scale leave it automatically honest; the
   * nonPositive count explains where they went (SSP-SCALE-003).
   * @param {number} [nonPositive=0] Pairs removed because a coordinate was
   *   zero or negative under the log scale.
   * @private
   */
  updateNotes(nonPositive = 0) {
    const totalParticipants = unique(this.cleanData.map((row) => row[this.settings.id_col])).length;
    const shownParticipants = this.chartPairs.length;
    const pct = totalParticipants
      ? ((shownParticipants / totalParticipants) * 100).toFixed(1)
      : '0.0';
    const removedNote = this.removedRecords
      ? `<span class="sv-warning">${this.removedRecords} missing or non-numeric results removed.</span>`
      : '';
    const nonPositiveNote = nonPositive
      ? `<span class="sv-warning">${nonPositive} participant pair${nonPositive > 1 ? 's' : ''} with a nonpositive value removed for the log scale.</span>`
      : '';
    this.notes.innerHTML = `<span>${shownParticipants} of ${totalParticipants} participants shown (${pct}%).</span>${removedNote}${nonPositiveNote}`;
  }

  /**
   * Resize the live chart to its container. For host layouts that change the
   * container size without a window resize — e.g. the R htmlwidget bindings.
   * @returns {void}
   */
  resize() {
    if (this.chart) this.chart.resize();
  }

  /**
   * Destroy the live Chart.js instance and detach its brush listeners without
   * touching the shell.
   * @private
   */
  destroyChart() {
    if (this.chart) {
      if (this.chart.$sspBrushCleanup) this.chart.$sspBrushCleanup();
      this.chart.destroy();
      this.chart = null;
    }
  }

  /**
   * Tear the shift plot down: destroy the Chart.js instance and empty the
   * target element. The instance cannot be reused afterwards — create a new
   * one via the factory instead.
   * @returns {void}
   */
  destroy() {
    unmountProfileRail(this);
    this.destroyChart();
    this.element.innerHTML = '';
  }
}

/**
 * Create a safety shift plot inside a container element. The control shell
 * renders immediately; pass long-format result records to setData (or init) on
 * the returned instance to validate the data and draw the scatter.
 * @param {string|HTMLElement} [element='body'] Container node, or a CSS selector for it.
 * @param {ShiftPlotSettings} [settings={}] Setting overrides, merged onto DEFAULT_SETTINGS and normalized.
 * @returns {SafetyShiftPlot} The live shift-plot instance.
 * @throws {Error} When no element matches the target selector.
 */
export default function shiftPlot(element = 'body', settings = {}) {
  return new SafetyShiftPlot(element, settings);
}
